import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Input } from '../Input/index.js';
import { ensurePortalRoot } from '../shared/index.js';
import {
  MOBILE_FLOATING_PANEL_STYLE,
  useMobileFloatingSheet,
} from '../shared/mobileFloating.js';
import { useFloating } from '../../hooks/useFloating.js';
import { useOutsideClick } from '../../hooks/useOutsideClick.js';
import { Empty } from '../DataView/index.js';

export interface ComboboxOption<V = string> {
  value: V;
  label: string;
  disabled?: boolean;
  description?: string;
}

export interface ComboboxProps<V = string> {
  /** Suggestions list. */
  options: ReadonlyArray<ComboboxOption<V>>;
  /** Controlled selected value (one of the option values). Null / undefined
   *  when nothing is chosen yet. */
  value?: V | null;
  /** Uncontrolled default. */
  defaultValue?: V | null;
  /** Fires when a suggestion is selected. */
  onValueChange?: (value: V | null) => void;
  /** Controlled input text (decoupled from selection — Combobox allows
   *  free text that does not match any option). */
  inputValue?: string;
  /** Fires for every keystroke. */
  onInputValueChange?: (value: string) => void;
  /** Override the default matcher. Default is case-insensitive substring on
   *  `option.label`. Return true to include the option. */
  filter?: (option: ComboboxOption<V>, query: string) => boolean;
  /** Allow free text that doesn't match any option. Default true. When
   *  false, blurring the input without a selection reverts to the last
   *  committed value. */
  allowFreeText?: boolean;
  /** Placeholder for the input. */
  placeholder?: string;
  /** Accessible label. */
  'aria-label'?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Content when no options match the query. Defaults to `<Empty>`. */
  emptyContent?: ReactNode;
  className?: string;
}

const DEFAULT_FILTER = <V,>(opt: ComboboxOption<V>, q: string): boolean =>
  opt.label.toLowerCase().includes(q.toLowerCase());

/** Free-text Input backed by a filterable dropdown of suggestions.
 *  Spec 15 §6. Differs from Select: accepts arbitrary values, filter is
 *  part of the flow, dropdown opens on focus or ArrowDown. */
export function Combobox<V = string>(props: ComboboxProps<V>): ReactNode {
  const {
    options,
    value: controlledSelection,
    defaultValue = null,
    onValueChange,
    inputValue: controlledInput,
    onInputValueChange,
    filter = DEFAULT_FILTER,
    allowFreeText = true,
    placeholder,
    disabled = false,
    invalid = false,
    size = 'md',
    emptyContent,
    className,
  } = props;

  const isSelectionControlled = controlledSelection !== undefined;
  const isInputControlled = controlledInput !== undefined;

  const [internalSelection, setInternalSelection] = useState<V | null>(defaultValue);
  const selection = isSelectionControlled ? controlledSelection : internalSelection;

  const [internalInput, setInternalInput] = useState<string>(() => {
    const found = options.find((o) => o.value === defaultValue);
    return found?.label ?? '';
  });
  const inputValue = isInputControlled ? controlledInput : internalInput;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;

  const { setAnchor, setFloating, floatingStyle, maxHeight, update } = useFloating({
    open,
    placement: 'bottom-start',
    matchAnchorWidth: true,
  });
  const mobileSheet = useMobileFloatingSheet(open);

  useEffect(() => {
    if (!open) return;
    update();
  }, [inputValue, open, update]);

  const filtered = useMemo(
    () => options.filter((o) => filter(o, inputValue)),
    [filter, inputValue, options],
  );

  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(0);
  }, [activeIndex, filtered.length]);

  const commit = useCallback(
    (next: V | null, nextLabel?: string): void => {
      if (!isSelectionControlled) setInternalSelection(next);
      onValueChange?.(next);
      const label =
        nextLabel ?? (next === null ? '' : (options.find((o) => o.value === next)?.label ?? ''));
      if (!isInputControlled) setInternalInput(label);
      onInputValueChange?.(label);
    },
    [isInputControlled, isSelectionControlled, onInputValueChange, onValueChange, options],
  );

  const handleInput = useCallback(
    (next: string): void => {
      if (!isInputControlled) setInternalInput(next);
      onInputValueChange?.(next);
      if (!open) setOpen(true);
      if (allowFreeText && !isSelectionControlled) {
        const match = options.find((o) => o.label === next);
        const nextValue = match === undefined ? null : match.value;
        // Only fire onValueChange when the resolved value actually
        // changes — both for the match→null transition (previously
        // silent: the parent thought the old option was still selected
        // even though internal state had cleared) and to avoid
        // chattering on every keystroke that doesn't touch selection.
        const currentInternal = internalSelection;
        if (nextValue !== currentInternal) {
          setInternalSelection(nextValue);
          onValueChange?.(nextValue);
        }
      }
    },
    [
      allowFreeText,
      isInputControlled,
      isSelectionControlled,
      internalSelection,
      onInputValueChange,
      onValueChange,
      open,
      options,
    ],
  );

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter' && open) {
        const picked = filtered[activeIndex];
        if (picked !== undefined && picked.disabled !== true) {
          e.preventDefault();
          commit(picked.value, picked.label);
          setOpen(false);
        }
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    },
    [activeIndex, commit, filtered, open],
  );

  useOutsideClick(
    panelRef,
    useCallback(() => {
      setOpen(false);
      if (!allowFreeText) {
        // Revert partial text to last committed.
        const label =
          selection === null ? '' : (options.find((o) => o.value === selection)?.label ?? '');
        if (!isInputControlled) setInternalInput(label);
      }
    }, [allowFreeText, isInputControlled, options, selection]),
    { enabled: open, extraRefs: [rootRef] },
  );

  const portalRoot = typeof document !== 'undefined' ? ensurePortalRoot() : null;
  const classes = ['vq-combobox', className].filter(Boolean).join(' ');

  return (
    <div
      ref={(el) => {
        rootRef.current = el;
        setAnchor(el);
      }}
      className={classes}
      data-component="Combobox"
      data-state={disabled ? 'disabled' : invalid ? 'invalid' : open ? 'open' : 'idle'}
    >
      <Input
        size={size}
        placeholder={placeholder}
        disabled={disabled}
        invalid={invalid}
        value={inputValue}
        onValueChange={handleInput}
        onFocus={() => {
          setOpen(true);
        }}
        onKeyDown={handleKey}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-activedescendant={
          open && filtered[activeIndex] !== undefined
            ? `${reactId}-opt-${String(activeIndex)}`
            : undefined
        }
        aria-label={props['aria-label']}
      />
      {open && portalRoot !== null
        ? createPortal(
            <div
              ref={(el) => {
                panelRef.current = el;
                setFloating(el);
              }}
              id={listboxId}
              role="listbox"
              className="vq-combobox__panel"
              data-mobile-sheet={mobileSheet ? 'true' : undefined}
              style={
                mobileSheet
                  ? MOBILE_FLOATING_PANEL_STYLE
                  : {
                      ...floatingStyle,
                      // Cap to the smaller of viewport availableHeight and the
                      // 320px design ceiling so a giant viewport doesn't yield a
                      // half-screen suggestion list. CSS still lists 320 as a
                      // belt-and-suspenders fallback if the inline math is 0.
                      ...(maxHeight > 0
                        ? { maxHeight: `${String(Math.min(maxHeight, 320))}px` }
                        : {}),
                    }
              }
            >
              {filtered.length === 0
                ? (emptyContent ?? <Empty size="compact" />)
                : filtered.map((opt, i) => (
                    <div
                      key={String(opt.value)}
                      id={`${reactId}-opt-${String(i)}`}
                      role="option"
                      aria-selected={opt.value === selection}
                      aria-disabled={opt.disabled}
                      data-active={i === activeIndex ? 'true' : undefined}
                      className="vq-combobox__option"
                      onMouseEnter={() => {
                        setActiveIndex(i);
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (opt.disabled !== true) {
                          commit(opt.value, opt.label);
                          setOpen(false);
                        }
                      }}
                    >
                      <div className="vq-combobox__label">{opt.label}</div>
                      {opt.description !== undefined ? (
                        <div className="vq-combobox__desc">{opt.description}</div>
                      ) : null}
                    </div>
                  ))}
            </div>,
            portalRoot,
          )
        : null}
    </div>
  );
}
